import {
  gxAiHealthApi,
  gxAiqueryApi,
} from "@/app/apiConfig/services/aiAbstractApiLayer";
import { ICON_BASE_PATH } from "@/app/utils/config";
import { useEffect, useRef, useState } from "react";
import "./CfoDashboard.css";

// --------------------------renderMarkdown-------------------------------------------------
const renderMarkdown = (text: string): React.ReactNode => {
  const normalised = text.replace(/\\n/g, "\n");
  const lines = normalised.split("\n");
  const nodes: React.ReactNode[] = [];

  // --------------------------renderInline-------------------------------------------------

  const renderInline = (raw: string, baseKey: string): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    const pattern = /(\*\*(.+?)\*\*|`([^`]+)`)/g;
    let last = 0;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(raw)) !== null) {
      if (match.index > last) parts.push(raw.slice(last, match.index));

      if (match[0].startsWith("**")) {
        parts.push(
          <strong key={`${baseKey}-b${match.index}`}>{match[2]}</strong>,
        );
      } else {
        parts.push(
          <code
            key={`${baseKey}-c${match.index}`}
            className="gx-ai-inline-code"
          >
            {match[3]}
          </code>,
        );
      }
      last = match.index + match[0].length;
    }

    if (last < raw.length) parts.push(raw.slice(last));
    return <>{parts}</>;
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line === "") {
      nodes.push(<div key={i} className="gx-ai-spacer" />);
      i++;
      continue;
    }

    nodes.push(
      <p key={i} className="gx-ai-p">
        {renderInline(line, `p${i}`)}
      </p>,
    );
    i++;
  }
  return <>{nodes}</>;
};

// ------------------------------ChatUI---------------------------------------------

const ChatUI = () => {
  const [messages, setMessages] = useState<any[]>([
    { sender: "admin", text: "What can I help with?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const typewriterRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const charQueueRef = useRef<string[]>([]);

  // ------------------------------getgxAiHealthApi---------------------------------------------

  const getgxAiHealthApi = async () => {
    try {
      const response = await gxAiHealthApi();
      console.log(response, "response");
    } catch (error) {}
  };

  // ------------------------------handleSend---------------------------------------------

  const getDocRefId = (): string => {
    let docRefId = sessionStorage.getItem("docRefId");

    if (!docRefId) {
      const bytes = crypto.getRandomValues(new Uint8Array(24));

      docRefId = btoa(String.fromCharCode(...bytes))
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=/g, "");

      sessionStorage.setItem("docRefId", docRefId);
    }

    return docRefId;
  };

  useEffect(() => {
    sessionStorage.removeItem("docRefId");
    getDocRefId();
  }, []);

  const handleSend = async () => {
    if (!input || loading) return;

    const userText = input;

    setMessages((prev) => [
      ...prev,
      { sender: "user", text: userText },
      { sender: "admin", text: "", typing: true },
    ]);

    setInput("");
    setLoading(true);

    try {
      const payload = {
        query: userText,
        queryId: "fe-query-sd",
        timestamp: new Date().toISOString(),
        session_id: getDocRefId(),
      };

      const stream = await gxAiqueryApi(payload);
      const reader = stream.getReader();
      const decoder = new TextDecoder("utf-8");

      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const decodedChunk = decoder.decode(value, { stream: true });
        buffer += decodedChunk;
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data:")) {
            let fragment = line.slice(5);
            if (fragment.trim() === "[DONE]") continue;
            charQueueRef.current.push(...fragment.split(""));
          }
        }

        if (!typewriterRef.current) {
          typewriterRef.current = setInterval(() => {
            if (charQueueRef.current.length === 0) return;

            const nextChar = charQueueRef.current.shift()!;
            setMessages((prev) => {
              const newMessages = [...prev];
              const lastIndex = newMessages.length - 1;
              newMessages[lastIndex] = {
                ...newMessages[lastIndex],
                text: (newMessages[lastIndex].text || "") + nextChar,
                typing: false,
              };
              return newMessages;
            });
          }, 10);
        }
      }

      setLoading(false);
    } catch (err) {
      console.error("Streaming Error:", err);
      setMessages((prev) => {
        const newMessages = [...prev];
        const lastIndex = newMessages.length - 1;
        newMessages[lastIndex] = {
          sender: "admin",
          text: "Error contacting AI service.",
          typing: false,
        };
        return newMessages;
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    getgxAiHealthApi();
    return () => {
      if (typewriterRef.current) clearInterval(typewriterRef.current);
    };
  }, []);

  return (
    <div className="gx-chatbot-ui">
      <div className="ai-panel">
        <div className="ai-header">
          <h2 className="gx-h3-b">Ask Genie</h2>
        </div>

        <div className="chat-body">
          {messages?.map((msg, index) => (
            <div key={index} className={`chat-message ${msg.sender}`}>
              <div className="bubble">
                {msg.typing ? (
                  <div className="typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                ) : (
                  <div className="gx-ai-md">
                    {renderMarkdown(msg.text ?? "")}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>

        <div className="ai-input-box">
          <span className="icon">
            <img src={`${ICON_BASE_PATH}aifile.svg`} alt="file" />
          </span>

          <input
            type="text"
            placeholder={loading ? "Reply to Growx" : "Ask Growx"}
            value={input}
            disabled={loading}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
          />

          <span className="icon">
            <img src={`${ICON_BASE_PATH}aimike.svg`} alt="mic" />
          </span>

          <button
            className="send-circle"
            disabled={loading}
            onClick={handleSend}
          >
            ➔
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatUI;
